import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/hooks/useAuth';
import { useHasPermission } from '@/hooks/permissions';
import { formatCurrency } from '@/utils/formatters';
import { formatDate } from '@/utils/dateUtils';
import { useTeamMemberProfile } from '@/hooks/team';
import { useProfileMutations } from '@/hooks/profile';
import { useUpdateUserRole } from '@/hooks/profile/useUpdateUserRole';
import { AvatarUpload } from './AvatarUpload';
import { RoleSelector } from './RoleSelector';
import { HourlyRateDisplay } from '@/components/team/HourlyRateDisplay';
import type { TeamMember } from '@/services/team/types';
import type { ProfileFormData } from '@/services/profile/types';
import { toast } from 'sonner';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Briefcase, 
  DollarSign,
  UserCheck,
  AlertTriangle,
  Heart,
  Cake,
  Edit
} from 'lucide-react';

interface ViewProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamMember: TeamMember;
  initialMode?: 'view' | 'edit';
}

export const ViewProfileDialog = ({ 
  open, 
  onOpenChange, 
  teamMember, 
  initialMode = 'view' 
}: ViewProfileDialogProps) => {
  const { profile: currentUserProfile } = useAuth();
  const { updateUserProfile, uploadAvatar, deleteAvatar } = useProfileMutations();
  const updateUserRole = useUpdateUserRole();
  // Permission-based access control (replaces legacy role checks)
  const canViewProfiles = useHasPermission('view_employee_details');
  const canEditProfiles = useHasPermission('manage_employee_details');
  const canViewPayRates = useHasPermission('manage_employee_pay');
  const canManageRoles = useHasPermission('manage_employee_roles');
  
  // Check if viewing own profile (prevent self-role-escalation)
  const isOwnProfile = currentUserProfile?.id === teamMember.id;

  // Component state
  const [mode, setMode] = useState<'view' | 'edit'>(initialMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [avatarError, setAvatarError] = useState<string>('');
  const [pendingRoleChange, setPendingRoleChange] = useState<{ roleId: string; roleName: string } | null>(null);
  const [formData, setFormData] = useState<ProfileFormData>({
    preferredName: '',
    firstName: '',
    lastName: '',
    address: '',
    email: '',
    mobilePhone: '',
    dateOfBirth: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
  });

  // Fetch complete profile data
  const { data: detailedProfile, isLoading, error } = useTeamMemberProfile(
    open ? teamMember.id : null
  );

  // Initialize form data when profile changes or mode switches to edit
  useEffect(() => {
    if (detailedProfile && mode === 'edit') {
      setFormData({
        preferredName: detailedProfile.preferred_name || '',
        firstName: detailedProfile.first_name || '',
        lastName: detailedProfile.last_name || '',
        address: detailedProfile.address || '',
        email: detailedProfile.email || '',
        mobilePhone: detailedProfile.mobile_phone || '',
        dateOfBirth: detailedProfile.date_of_birth || '',
        emergencyContactName: detailedProfile.emergency_contact_name || '',
        emergencyContactPhone: detailedProfile.emergency_contact_phone || '',
      });
    }
  }, [detailedProfile, mode]);

  // Reset mode when dialog closes
  useEffect(() => {
    if (!open) {
      setMode('view');
      setAvatarError('');
      setPendingRoleChange(null);
    }
  }, [open]);

  // Prevent unauthorized access
  if (!canViewProfiles) {
    return null;
  }

  // Form submission handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!detailedProfile) return;
    
    setIsSubmitting(true);

    try {
      const updateInput = {
        preferredName: formData.preferredName,
        firstName: formData.firstName,
        lastName: formData.lastName,
        address: formData.address,
        email: formData.email,
        mobilePhone: formData.mobilePhone,
        dateOfBirth: formData.dateOfBirth,
        emergencyContactName: formData.emergencyContactName,
        emergencyContactPhone: formData.emergencyContactPhone,
      };

      // CRITICAL: Use profile.user_id for RPC calls (matches auth.users.id)
      await updateUserProfile.mutateAsync({
        authUserId: detailedProfile.user_id, // Use profile.user_id for RPC
        input: updateInput,
        profileId: detailedProfile.id, // Pass profile.id for correct cache invalidation
      });
      
      // If there's a pending role change, apply it
      if (pendingRoleChange && !isOwnProfile) {
        await updateUserRole.mutateAsync({
          profileId: detailedProfile.id,
          roleId: pendingRoleChange.roleId,
          roleName: pendingRoleChange.roleName,
        });
        setPendingRoleChange(null);
      }
      
      setMode('view'); // Switch back to view mode
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Role change handler
  const handleRoleChange = (roleId: string, roleName: string) => {
    setPendingRoleChange({ roleId, roleName });
  };

  // Form input change handler
  const handleInputChange = (field: keyof ProfileFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Avatar upload handler
  const handleAvatarUpload = async (file: File) => {
    if (!detailedProfile || !currentUserProfile?.company_id) return;
    
    setAvatarError('');
    try {
      // CRITICAL: Use profile.user_id for storage operations (RLS compliance)
      await uploadAvatar.mutateAsync({
        file,
        authUserId: detailedProfile.user_id, // Use profile.user_id for RLS compliance
        companyId: currentUserProfile.company_id,
        targetAuthUserId: isOwnProfile ? undefined : detailedProfile.user_id,
        profileId: detailedProfile.id, // cache invalidation only
      });
    } catch (error) {
      setAvatarError(error instanceof Error ? error.message : 'Failed to upload avatar');
    }
  };

  // Avatar remove handler
  const handleAvatarRemove = async () => {
    if (!detailedProfile?.avatar_url) return;
    
    setAvatarError('');
    try {
      await deleteAvatar.mutateAsync({
        avatarUrl: detailedProfile.avatar_url,
        targetAuthUserId: isOwnProfile ? undefined : detailedProfile.user_id,
        profileId: detailedProfile.id,
      });
    } catch (error) {
      setAvatarError(error instanceof Error ? error.message : 'Failed to remove avatar');
    }
  };

  const getUserInitials = (member: TeamMember) => {
    const firstName = member.first_name || '';
    const lastName = member.last_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  };

  const getDisplayName = (member: TeamMember) => {
    if (member.first_name && member.last_name) {
      return `${member.first_name} ${member.last_name}`;
    }
    return member.email.split('@')[0];
  };

  const getDetailedDisplayName = () => {
    if (!detailedProfile) return getDisplayName(teamMember);
    
    const preferredName = detailedProfile.preferred_name;
    const firstName = detailedProfile.first_name;
    const lastName = detailedProfile.last_name;
    
    if (preferredName) {
      return preferredName;
    }
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    return detailedProfile.email.split('@')[0];
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
        return 'destructive';
      case 'manager':
        return 'default';
      case 'supervisor':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getStatusBadgeVariant = (status: string | null) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'default';
      case 'inactive':
      case 'archived':
        return 'secondary';
      case 'pending':
        return 'outline';
      default:
        return 'outline';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-3">
              <User className="h-5 w-5" />
              {getDisplayName(teamMember)}'s Profile
            </DialogTitle>
            
            {/* Edit Button - Only show for managers+ and in view mode */}
            {canEditProfiles && mode === 'view' && !isLoading && detailedProfile && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMode('edit')}
                className="flex items-center gap-2"
              >
                <Edit className="h-4 w-4" />
                Edit Profile
              </Button>
            )}
          </div>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Loading State */}
          {isLoading && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                <Skeleton className="h-16 w-16 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-6 w-48" />
                  <Skeleton className="h-4 w-64" />
                </div>
              </div>
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <div className="text-sm text-red-800">
                <p className="font-medium">Error Loading Profile</p>
                <p>Unable to load complete profile information. Showing basic details only.</p>
              </div>
            </div>
          )}

          {/* Conditional Content Based on Mode */}
          {mode === 'view' ? (
            <>
              {/* Profile Header - View Mode */}
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={(detailedProfile?.avatar_url || teamMember.avatar_url) || undefined} />
                  <AvatarFallback className="text-lg">
                    {getUserInitials(teamMember)}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-xl font-semibold">
                      {getDetailedDisplayName()}
                    </h3>
                    <Badge variant={getRoleBadgeVariant(detailedProfile?.role || teamMember.role)}>
                      {detailedProfile?.role || teamMember.role}
                    </Badge>
                    {(detailedProfile?.status || teamMember.status) && (
                      <Badge variant={getStatusBadgeVariant(detailedProfile?.status || teamMember.status)}>
                        {(detailedProfile?.status || teamMember.status)!.toUpperCase()}
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    <span>{detailedProfile?.email || teamMember.email}</span>
                  </div>
                </div>
              </div>

              {/* Personal Information Section - View Mode */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Personal Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 border rounded-lg">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">First Name</p>
                        <p className="text-sm text-muted-foreground">
                          {detailedProfile?.first_name || teamMember.first_name || 'Not provided'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 border rounded-lg">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Preferred Name</p>
                        <p className="text-sm text-muted-foreground">
                          {detailedProfile?.preferred_name || 'Not provided'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 border rounded-lg">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Mobile Phone</p>
                        <p className="text-sm text-muted-foreground">
                          {detailedProfile?.mobile_phone || 'Not provided'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 border rounded-lg">
                      <Cake className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Date of Birth</p>
                        <p className="text-sm text-muted-foreground">
                          {detailedProfile?.date_of_birth ? formatDate(detailedProfile.date_of_birth) : 'Not provided'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 border rounded-lg">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Last Name</p>
                        <p className="text-sm text-muted-foreground">
                          {detailedProfile?.last_name || teamMember.last_name || 'Not provided'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 border rounded-lg">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Email Address</p>
                        <p className="text-sm text-muted-foreground">
                          {detailedProfile?.email || teamMember.email}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-3 border rounded-lg">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">Address</p>
                        <p className="text-sm text-muted-foreground">
                          {detailedProfile?.address || 'Not provided'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 border rounded-lg">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Join Date</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(detailedProfile?.created_at || teamMember.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Emergency Contact Information - View Mode */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <Heart className="h-5 w-5" />
                  Emergency Contact
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Contact Name</p>
                      <p className="text-sm text-muted-foreground">
                        {detailedProfile?.emergency_contact_name || 'Not provided'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Contact Phone</p>
                      <p className="text-sm text-muted-foreground">
                        {detailedProfile?.emergency_contact_phone || 'Not provided'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Work Information Section - View Mode */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Work Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <UserCheck className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Role</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-muted-foreground">{detailedProfile?.role || teamMember.role}</p>
                        <Badge variant={getRoleBadgeVariant(detailedProfile?.role || teamMember.role)} className="text-xs">
                          {detailedProfile?.role || teamMember.role}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <UserCheck className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Status</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-muted-foreground">
                          {detailedProfile?.status || teamMember.status || 'Active'}
                        </p>
                        <Badge variant={getStatusBadgeVariant(detailedProfile?.status || teamMember.status)} className="text-xs">
                          {(detailedProfile?.status || teamMember.status || 'Active').toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pay Rate - Only for Admins */}
                {canViewPayRates && (
                  <div className="flex items-center gap-3 p-3 border rounded-lg bg-green-50 border-green-200">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-green-800">Pay Rate</p>
                      <HourlyRateDisplay 
                        profileId={detailedProfile?.id || teamMember.id} 
                        className="text-lg font-semibold text-green-900"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Dialog Actions - View Mode */}
              <div className="flex justify-end pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Close
                </Button>
              </div>
            </>
          ) : (
            /* Edit Mode */
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Avatar Upload Section - Edit Mode */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Profile Picture
                </h3>
                
                <AvatarUpload
                  currentAvatarUrl={detailedProfile?.avatar_url}
                  userInitials={getUserInitials(teamMember)}
                  onFileSelect={handleAvatarUpload}
                  onRemove={handleAvatarRemove}
                  isUploading={uploadAvatar.isPending}
                  error={avatarError}
                />
              </div>

              {/* Personal Information Section - Edit Mode */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Personal Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name</Label>
                      <Input
                        id="firstName"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        placeholder="Enter first name"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="preferredName">Preferred Name</Label>
                      <Input
                        id="preferredName"
                        value={formData.preferredName}
                        onChange={(e) => handleInputChange('preferredName', e.target.value)}
                        placeholder="Enter preferred name"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="mobilePhone">Mobile Phone</Label>
                      <Input
                        id="mobilePhone"
                        type="tel"
                        value={formData.mobilePhone}
                        onChange={(e) => handleInputChange('mobilePhone', e.target.value)}
                        placeholder="Enter mobile phone"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="dateOfBirth">Date of Birth</Label>
                      <Input
                        id="dateOfBirth"
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        placeholder="Enter last name"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="Enter email address"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="address">Address</Label>
                      <Textarea
                        id="address"
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        placeholder="Enter address"
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Emergency Contact Information - Edit Mode */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <Heart className="h-5 w-5" />
                  Emergency Contact
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="emergencyContactName">Contact Name</Label>
                    <Input
                      id="emergencyContactName"
                      value={formData.emergencyContactName}
                      onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
                      placeholder="Enter emergency contact name"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="emergencyContactPhone">Contact Phone</Label>
                    <Input
                      id="emergencyContactPhone"
                      type="tel"
                      value={formData.emergencyContactPhone}
                      onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
                      placeholder="Enter emergency contact phone"
                    />
                  </div>
                </div>
              </div>

              {/* Role Assignment Section - Edit Mode (only for other users) */}
              {canManageRoles && !isOwnProfile && detailedProfile && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium flex items-center gap-2">
                    <Briefcase className="h-5 w-5" />
                    Role Assignment
                  </h3>
                  
                  <RoleSelector
                    currentRoleId={pendingRoleChange?.roleId || detailedProfile.role_id}
                    currentRoleName={pendingRoleChange?.roleName || detailedProfile.role}
                    onRoleChange={handleRoleChange}
                    disabled={isSubmitting}
                    isLoading={updateUserRole.isPending}
                  />
                </div>
              )}

              {/* Dialog Actions - Edit Mode */}
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setMode('view')}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
